import { spawn } from 'node:child_process'
import { stripVTControlCharacters } from 'node:util'

import type {
  RailwayRunMode,
  RailwayRunner,
  RunRailwayResult
} from '@next-adapter/init/railway/types.js'

export const RAILWAY_CLI_VERSION = '5.26.4'
const DEFAULT_TIMEOUT_MS = 120_000

function pathRunner(version: string): RailwayRunner {
  return { bin: 'railway', prefix: [], source: 'path', version }
}

function npxRunner(): RailwayRunner {
  return {
    bin: 'npx',
    prefix: ['-y', `@railway/cli@${RAILWAY_CLI_VERSION}`],
    source: 'npx',
    version: RAILWAY_CLI_VERSION
  }
}

function parseVersion(output: string): string | undefined {
  return output.match(/\b(\d+\.\d+\.\d+)\b/)?.[1]
}

function compareVersions(left: string, right: string): number {
  const leftParts = left.split('.').map(Number)
  const rightParts = right.split('.').map(Number)
  for (let index = 0; index < 3; index++) {
    const difference = (leftParts[index] ?? 0) - (rightParts[index] ?? 0)
    if (difference !== 0) return difference
  }
  return 0
}

export function isCompatibleRailwayVersion(version: string): boolean {
  return version.startsWith('5.') && compareVersions(version, RAILWAY_CLI_VERSION) >= 0
}

export async function resolveRailwayRunner(): Promise<RailwayRunner> {
  const probeRunner = pathRunner(RAILWAY_CLI_VERSION)
  const probe = await runRailway(probeRunner, ['--version'], {
    cwd: process.cwd(),
    mode: 'capture',
    timeoutMs: 10_000
  })
  const version = parseVersion(`${probe.stdout}\n${probe.stderr}`)

  return probe.success && version && isCompatibleRailwayVersion(version)
    ? pathRunner(version)
    : npxRunner()
}

export function runRailway(
  runner: RailwayRunner,
  args: string[],
  options: {
    cwd: string
    mode: RailwayRunMode
    timeoutMs?: number
    env?: NodeJS.ProcessEnv
    stdinInput?: string
    onOutputLine?: (line: string) => void
  }
): Promise<RunRailwayResult> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS
  const argv = [...runner.prefix, ...args]

  return new Promise((resolve) => {
    const child = spawn(runner.bin, argv, {
      cwd: options.cwd,
      stdio: options.mode === 'inherit' ? 'inherit' : 'pipe',
      env: { ...process.env, npm_config_loglevel: 'error', ...options.env }
    })

    let stdout = ''
    let stderr = ''
    let timedOut = false
    let stdoutPending = ''
    let stderrPending = ''
    let settled = false
    let forceKillTimer: NodeJS.Timeout | undefined

    const observe = (pending: string, chunk: string): string => {
      if (!options.onOutputLine) return pending
      const parts = `${pending}${chunk}`.split(/\r\n|\n|\r/)
      const nextPending = parts.pop() ?? ''
      for (const part of parts) {
        const line = stripVTControlCharacters(part).trim()
        if (line) options.onOutputLine(line)
      }
      return nextPending
    }

    if (options.mode === 'capture') {
      if (options.stdinInput !== undefined) {
        child.stdin?.write(options.stdinInput)
      }
      child.stdin?.end()

      child.stdout?.on('data', (chunk: Buffer) => {
        const text = chunk.toString()
        stdout += text
        stdoutPending = observe(stdoutPending, text)
      })
      child.stderr?.on('data', (chunk: Buffer) => {
        const text = chunk.toString()
        stderr += text
        stderrPending = observe(stderrPending, text)
      })
    }

    const timeout = setTimeout(() => {
      timedOut = true
      child.kill('SIGTERM')
      forceKillTimer = setTimeout(() => child.kill('SIGKILL'), 2_000)
    }, timeoutMs)

    const finish = (code: number | null, signal: NodeJS.Signals | null, errorMessage?: string) => {
      if (settled) return
      settled = true
      clearTimeout(timeout)
      if (forceKillTimer) clearTimeout(forceKillTimer)
      for (const pending of [stdoutPending, stderrPending]) {
        const line = stripVTControlCharacters(pending).trim()
        if (line) options.onOutputLine?.(line)
      }
      resolve({
        success: !timedOut && !errorMessage && code === 0,
        code,
        signal,
        stdout,
        stderr,
        timedOut,
        errorMessage:
          errorMessage ??
          (timedOut
            ? `Railway command timed out after ${Math.floor(timeoutMs / 1000)}s`
            : code === 0
              ? undefined
              : signal
                ? `Railway command terminated by ${signal}`
                : `Railway command exited with code ${code ?? 'unknown'}`)
      })
    }

    child.on('close', (code, signal) => finish(code, signal))
    child.on('error', (error) => finish(null, null, error.message))
  })
}

export function parseRailwayJson<T = unknown>(output: string): T | undefined {
  const trimmed = stripVTControlCharacters(output).trim()
  if (!trimmed) return undefined

  try {
    return JSON.parse(trimmed) as T
  } catch {
    // Railway writes selection/progress lines separately on some commands.
  }

  const objectStart = trimmed.indexOf('{')
  const arrayStart = trimmed.indexOf('[')
  const starts = [objectStart, arrayStart].filter((value) => value >= 0)
  if (starts.length === 0) return undefined
  const start = Math.min(...starts)
  const opener = trimmed[start]
  const end = opener === '{' ? trimmed.lastIndexOf('}') : trimmed.lastIndexOf(']')
  if (end <= start) return undefined

  try {
    return JSON.parse(trimmed.slice(start, end + 1)) as T
  } catch {
    return undefined
  }
}

export function railwayOutputTail(output: string, maxLines = 20): string | undefined {
  const lines = stripVTControlCharacters(output)
    .split(/\r?\n/)
    .map((line) => line.trimEnd())
    .filter((line) => line.trim().length > 0)
  return lines.length > 0 ? lines.slice(-maxLines).join('\n') : undefined
}
